import { HttpClient } from '@/shared/lib/http-client';
import { getAttributionPayload } from '@/features/analytics';
import type { SubscriptionResult } from '../types';
import type {
  IPaymentService,
  CreateCardSubscriptionParams,
  CreateApplePaySubscriptionParams,
  CreateUpsellSubscriptionParams,
  FinalizeSubscriptionParams,
} from './payment-service.interface';

export class PaymentService extends HttpClient implements IPaymentService {
  constructor(baseURL: string) {
    super({ baseURL });
  }

  async createCardSubscription(params: CreateCardSubscriptionParams): Promise<SubscriptionResult> {
    const data = await this.post<SubscriptionResult>('/subs/create-no-user', {
      plan_id: params.planId,
      email: params.email,
      instrument: params.instrument,
      attribution: getAttributionPayload(),
      address: {
        country: 'US',
        first_name: params.firstName || '',
        last_name: params.lastName || '',
      },
    });
    if (data.error) throw new Error(data.error);
    return data;
  }

  async createApplePaySubscription(params: CreateApplePaySubscriptionParams): Promise<SubscriptionResult> {
    const b = params.billing ?? {};
    const data = await this.post<SubscriptionResult>('/subs/create-no-user-applepay', {
      plan_id: params.planId,
      email: params.email,
      token: params.applePayToken,
      display_name: params.displayName ?? null,
      vault: 'payrails',
      attribution: getAttributionPayload(),
      address: {
        first_name: b.givenName || '',
        last_name: b.familyName || '',
        country: b.countryCode || 'US',
        state: b.administrativeArea || '',
        zip: b.postalCode || '',
        city: b.locality || '',
        address: (b.addressLines || [])[0] || '',
        phone: b.phoneNumber || '',
      },
    });
    if (data.error) throw new Error(data.error);
    return data;
  }

  async createUpsellSubscription(params: CreateUpsellSubscriptionParams): Promise<SubscriptionResult> {
    const data = await this.post<SubscriptionResult>('/subs/create-no-user', {
      plan_id: params.planId,
      email: params.email,
      subscription_id: params.subscriptionId,
      attribution: getAttributionPayload(),
      address: { country: 'US' },
    });
    if (data.error) throw new Error(data.error);
    return data;
  }

  async finalizeSubscription(params: FinalizeSubscriptionParams): Promise<SubscriptionResult> {
    const data = await this.post<SubscriptionResult>('/subs/finalize', {
      subscription_id: params.subscriptionId,
      email: params.email,
      firebase_id: params.firebaseId,
      apple_token: params.appleToken,
      plan: params.plan,
      attribution: getAttributionPayload(),
    });
    if (data.error) throw new Error(data.error);
    return data;
  }

  async validateApplePayMerchant(validationURL: string, domain: string): Promise<unknown> {
    return this.post('/api/apple-pay/validate-merchant', { validationURL, domain });
  }
}

export const paymentService: IPaymentService = new PaymentService(
  import.meta.env.VITE_PAYMENT_API_URL || '',
);
